import { Link, useNavigate, useParams, useSearchParams, useOutletContext } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react";

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from "../../components/ConfirmationModal.jsx";
import DebugAccordion from "../../components/DebugAccordion"
import JsonForm from "../../components/JsonForm.jsx";
import PageTitle from "../../components/PageTitle.jsx"
import ProfileNav from "./components/ProfileNav"
import ProfileTools from "./components/ProfileTools.jsx";
import { addNotification, NotificationTypes } from "../../stores/notification-store.js";
import { isEmptyObject, makeFieldsReadonly } from '../../utils/utils'
import { UpdateProfile } from "../../utils/api.js";
import { NOT_FOUND } from "../../utils/constants.js";

import useProfile from "../../hooks/useProfile.js"
import useProfileSchema, { profileSchema } from "../../hooks/useProfileSchema.js"
import useSchemaManifest from "../../hooks/useSchemaManifest.js"

export default function ProfileConfig() {

    const navigate = useNavigate()
    const { beId, profileId } = useParams()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { config, accessToken, canEditSignal, accordionData: sidebarAccordionData  } = useOutletContext()
    const { manifest } = useSchemaManifest()

    const { profile, loading: profileLoading, isLoaded: profileIsLoaded, modified, ccschema } = useProfile(beId, profileId, config, accessToken)
    const { loading: schemaLoading, isLoaded: schemaIsLoaded, version: profileVersion } = useProfileSchema(beId, ccschema.value, manifest.value, config, accessToken)

    const jsonFormData = useComputed(() => {

        if (!isEmptyObject(profileSchema.value) && !isEmptyObject(profile.value)) {
        
            // deep clone the schema
            let clonedSchema = structuredClone(profileSchema.value)

            // remove the api interfaces from the schema since those are edited in a separate page
            delete clonedSchema.properties.config.properties.apiInterfaceConfig

            // disable editing of all fields if canEditSignal is false
            if (!canEditSignal.value) {
                clonedSchema = makeFieldsReadonly(clonedSchema)
            }

            // sets the variable on the global windows object so that shift-utils.js code can determine if we are working with a profile or config schema
            globalThis.SCHEMA_MODE = 'PROFILE'
            console.log('Set SCHEMA_MODE to', globalThis.SCHEMA_MODE)
            
            // NOTE: if onSubmitValid is changed to onSubmit then the callback must also be changed to receive both errors and values
            const formData = {
                schema: clonedSchema.properties.config,
                value: profile.value.componentProfile.config,
                loadedComponentConfig: profile.value.componentProfile.config,
                onSubmitValid: handleFormSubmit,
                onCancel: handleCancel,
                onFormChange: handleFormChange,
                canEditConfig: canEditSignal.value
            }
            console.log('formData from config', formData)
            return formData
        }
    })

    const showCancelModal = useSignal(false)
    const isFormEdited = useSignal(false)
    const accordionData = [
        { name: 'Profile Data', data: profile },
        { name: 'Profile Schema', data: profileSchema },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        { name: 'Schema Manifest', data: manifest }
    ]

    const handleFormSubmit = values => {
        try {
            console.log('values', values)
            if (values) {
                
                // build the full config data including apiInterface and deviceConfig data to save back to server
                let data = {
                    id: profile.value.componentProfile.id,
                    name: profile.value.componentProfile.name,
                    businessEntityId: profile.value.componentProfile.businessEntityId,
                    config: values,
                }
                data.config.apiInterfaceConfig = profile.value.componentProfile.config.apiInterfaceConfig || {}
                data.config.apiInterfaceConfig.apiInterface = data.config.apiInterfaceConfig.apiInterface || []
                data.config.deviceConfig = profile.value.componentProfile.config.deviceConfig || {}
                data.config.deviceConfig.device = data.config.deviceConfig.device || []
                
                // add a schema value to the config data. This allows us to load this specific schema rather than a default one in future edits
                data.config.utgGlobal.ccschema = profileVersion.value

                console.log(data)
                UpdateProfile(beId, profileId, data, config, accessToken)
                    .then(result => {
                        modified.value = true
                        profile.value = result
                        addNotification('Profile was successfully updated.', NotificationTypes.SUCCESS, true)
                        navigate('../')
                    })
                    .catch(error => {
                        console.log(error.toString())
                        addNotification(`Unable to update the Profile. ${error.toString()}`, NotificationTypes.ERROR, false)
                        return false
                    })
                    .finally(() => {
                        // must return false to avoid postback
                        return false
                    })
            }
        }
        catch (error) {
            console.log(error)
        }

        // return false to prevent form submit from refreshing the page
        return false
    }

    const handleCancel = () => {
        if (isFormEdited.value) {
            showCancelModal.value = true
        }
        else {
            navigate('../')
        }
    }

    const handleFormChange = () => {
        isFormEdited.value = true
    }

    if (!profileIsLoaded.value || !schemaIsLoaded.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    return (
        <>
        <ConfirmationModal show={showCancelModal.value} handleCancel={() => showCancelModal.value = false} handleConfirm={() => navigate('../')} message={'You have unsaved changes to this form. Are you sure you want to leave and lose these changes?'} title={"Warning: Unsaved Changes"} cancelText="Continue Editing" confirmText="Lose Changes" />
        <Breadcrumb className="px-3 pt-1 bg-light">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/profiles` }}>
                Profile List
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Manage {profile.value.componentProfile.name}</Breadcrumb.Item>
        </Breadcrumb>
        <Stack direction="horizontal" gap={3}>
            <PageTitle title='Profile Configuration' canEditSignal={canEditSignal} />
            <ProfileTools profile={profile.value}></ProfileTools>
        </Stack>
        <ProfileNav beId={beId} profileId={profile.value.componentProfile.id} activeKey="4" debug={debug}></ProfileNav>
        <JsonForm data={jsonFormData.value}></JsonForm>
        <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}